import { Link } from "@/components/LocalizedLink";
import { ArrowUpRight, MoveRight } from "lucide-react";
import { Reveal, Overline } from "@/components/Kinetic";
import { useLang } from "@/i18n/LanguageContext";

const FLOW_EN = ["Product", "Channel", "Market", "Companies"];
const FLOW_TR = ["Ürün", "Kanal", "Pazar", "Şirketler"];
const BADGES_EN = ["Market intelligence", "Partner discovery", "Built for exporters"];
const BADGES_TR = ["Pazar istihbaratı", "İş ortağı keşfi", "İhracatçılar için"];

export default function ProductsSection() {
  const { lang, tx } = useLang();
  const flow = lang === "tr" ? FLOW_TR : FLOW_EN;
  const badges = lang === "tr" ? BADGES_TR : BADGES_EN;

  return (
    <section id="portfolio" className="border-t border-slate-200 bg-white py-20 sm:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Overline>{tx("Portfolio", "Portföy")}</Overline>
        </Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-6 font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl">
            {tx("A focused portfolio.", "Odaklı bir portföy.")}
          </h2>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-8">
          <Reveal className="lg:col-span-7" y={28}>
            <Link
              to="/products/cernio"
              data-testid="product-cernio-card"
              className="focus-ring group flex h-full flex-col justify-between bg-ink p-8 transition-colors duration-500 hover:bg-navy sm:p-10 lg:p-12"
            >
              <div>
                <div className="flex items-center justify-between gap-4">
                  <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-400">
                    {tx("Flagship product", "Amiral gemisi ürün")}
                  </span>
                  <ArrowUpRight
                    size={20}
                    className="text-slate-500 transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-white"
                  />
                </div>
                <h3 className="mt-8 font-display text-4xl font-bold tracking-tight text-white sm:text-5xl">
                  Cernio
                </h3>
                <p className="mt-5 max-w-md text-base leading-relaxed text-slate-300">
                  {tx(
                    "AI-powered B2B market and channel intelligence. Cernio helps exporters discover and evaluate the companies that should sell their product — and where.",
                    "Yapay zekâ destekli B2B pazar ve kanal istihbaratı. Cernio, ihracatçıların ürünlerini satması gereken şirketleri — ve nerede olduklarını — keşfetmesine ve değerlendirmesine yardımcı olur.",
                  )}
                </p>
                <div className="mt-7 flex flex-wrap gap-2">
                  {badges.map((b) => (
                    <span
                      key={b}
                      className="border border-white/15 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-slate-300"
                    >
                      {b}
                    </span>
                  ))}
                </div>
              </div>
              <div className="mt-12 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-white/10 pt-7">
                {flow.map((f, i) => (
                  <span key={f} className="flex items-center gap-3">
                    <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400">
                      {f}
                    </span>
                    {i < flow.length - 1 && (
                      <MoveRight size={14} className="text-gold" aria-hidden="true" />
                    )}
                  </span>
                ))}
              </div>
            </Link>
          </Reveal>

          <div className="flex flex-col gap-6 lg:col-span-5 lg:gap-8">
            <Reveal delay={0.12} y={28} className="flex-1">
              <Link
                to="/projects/dose-modernization"
                data-testid="product-dose-card"
                className="focus-ring group flex h-full flex-col justify-between border border-slate-200 bg-paper p-8 transition-colors duration-300 hover:border-ink sm:p-10"
              >
                <div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-400">
                      {tx("Corporate web experience", "Kurumsal web deneyimi")}
                    </span>
                    <ArrowUpRight
                      size={18}
                      className="text-slate-300 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-ink"
                    />
                  </div>
                  <h3 className="mt-6 font-display text-2xl font-bold tracking-tight text-ink">
                    DOSE Modernization
                  </h3>
                  <p className="mt-4 text-sm leading-relaxed text-slate-600">
                    {tx(
                      "Modernization of the corporate web presence of Atlantik Tüketim Malları and its industrial aerosol brand DOSE — a modern digital experience for an established operating business.",
                      "Atlantik Tüketim Malları ve endüstriyel aerosol markası DOSE için kurumsal web sitesinin yenilenmesi — köklü ve faal bir işletme için modern bir dijital deneyim.",
                    )}
                  </p>
                </div>
                <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400">
                  {tx(
                    "Selected work · Live at dose.com.tr",
                    "Seçili çalışma · dose.com.tr’de yayında",
                  )}
                </p>
              </Link>
            </Reveal>

            <Reveal delay={0.2} y={28} className="flex-1">
              <Link
                to="/projects/kampanyambenim"
                data-testid="product-kampanyambenim-card"
                className="focus-ring group flex h-full flex-col justify-between border border-slate-200 bg-paper p-8 transition-colors duration-300 hover:border-ink sm:p-10"
              >
                <div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-400">
                      {tx("Product initiative", "Ürün girişimi")}
                    </span>
                    <ArrowUpRight
                      size={18}
                      className="text-slate-300 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-ink"
                    />
                  </div>
                  <h3 className="mt-6 font-display text-2xl font-bold tracking-tight text-ink">
                    KampanyamBenim
                  </h3>
                  <p className="mt-4 text-sm leading-relaxed text-slate-600">
                    {tx(
                      "An early-stage initiative exploring how credit-card campaigns could become easier to understand and compare.",
                      "Kredi kartı kampanyalarını daha anlaşılır ve karşılaştırılabilir hâle getirmeyi amaçlayan, erken geliştirme aşamasında bir girişim.",
                    )}
                  </p>
                </div>
                <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400">
                  {tx("Early development · Not yet complete", "Erken geliştirme · Henüz tamamlanmadı")}
                </p>
              </Link>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
